import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import Table, { buttonsStyle, dateFormatter } from "../../../components/tables/table";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import {
  getCongresoEdiciones,
  deleteCongresoEdicion,
  setDefaultCongresoEdicion,
  updateCongresoEdicion,
  changeStatusCongresoEdicion,
  postCongresoPreviewToken,
} from "../../../../api/congresoEdicion";
import {
  Button,
  Tooltip,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import GetAppIcon from "@material-ui/icons/GetApp";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from "@material-ui/icons/Visibility";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { useHistory } from "react-router-dom";
import { shallowEqual, useSelector } from "react-redux";
import {
  canEditGestionModule,
  isReadOnlyConsultor,
  isCongresoDelegado,
  canCreateCongresoEdicion,
} from "../../../../_helpers/backofficePermissions";
import { getNonEmpty } from "../../../../utils/helpers";
import "./congresoEdicion.scss";

const actionTextBtnStyle = {
  maxWidth: "none",
  minWidth: "auto",
  width: "auto",
  minHeight: "30px",
  maxHeight: "30px",
  height: "30px",
  marginLeft: "0.65em",
  padding: "0 10px",
  textTransform: "none",
  fontSize: "0.8125rem",
  fontWeight: 500,
  whiteSpace: "nowrap",
  lineHeight: 1,
  backgroundColor: "#fff",
};

function getData(ediciones) {
  return ediciones.map((item) => ({
    year: item.year,
    titulo: getNonEmpty(item.titulo),
    isDefault: !!item.isDefault,
    published: !!item.published,
    active: item.active !== false,
    updatedAt: item.updatedAt,
    id: item._id,
    _id: item._id,
    myAccess: item.myAccess || null,
  }));
}

export default function CongresoEdicionesPage() {
  const [ediciones, setEdiciones] = useState([]);
  const [edicionTarget, setEdicionTarget] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [openNuevaEdicionDialog, setOpenNuevaEdicionDialog] = useState(false);
  const [nuevaEdicionModo, setNuevaEdicionModo] = useState("cero");
  const [plantillaId, setPlantillaId] = useState("");
  const history = useHistory();
  const user = useSelector((store) => store.authentication?.user, shallowEqual);

  const isStaffGestionEdits =
    canEditGestionModule(user?.role) && !isReadOnlyConsultor(user?.role);
  const isDelegado = isCongresoDelegado(user?.role);
  const canShowCreate = canCreateCongresoEdicion(user?.role);

  const canPublishRow = useCallback(
    (row) =>
      isStaffGestionEdits || (isDelegado && row?.myAccess?.canPublish),
    [isStaffGestionEdits, isDelegado]
  );
  const canDefaultRow = useCallback(
    (row) =>
      isStaffGestionEdits || (isDelegado && row?.myAccess?.canSetDefault),
    [isStaffGestionEdits, isDelegado]
  );
  const canDeleteRow = useCallback(
    (row) =>
      isStaffGestionEdits || (isDelegado && row?.myAccess?.canDelete),
    [isStaffGestionEdits, isDelegado]
  );

  const canToggleActiveRow = useCallback(
    (row) =>
      isStaffGestionEdits || (isDelegado && row?.myAccess?.canChangeStatus),
    [isStaffGestionEdits, isDelegado]
  );

  const abrirPreviewWeb = useCallback(async (edicionId) => {
    try {
      const res = await postCongresoPreviewToken(edicionId);
      if (res.status === 200 && res.data?.previewUrl) {
        window.open(res.data.previewUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      alertError({
        error,
        customMessage: "No se pudo generar la vista previa web pública.",
      });
    }
  }, []);

  const togglePublished = useCallback((row) => {
    const next = !row.published;
    updateCongresoEdicion(row._id, { published: next })
      .then((res) => {
        if (res.status === 200) {
          alertSuccess({
            title: "Hecho",
            customMessage: next
              ? "Edición marcada como publicada."
              : "Edición despublicada.",
          });
          setRefresh(true);
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "No se pudo actualizar el estado de publicación.",
        });
      });
  }, []);

  const toggleActive = useCallback((row) => {
    const next = !row.active;
    changeStatusCongresoEdicion(row._id, next)
      .then((res) => {
        if (res.status === 200) {
          alertSuccess({
            title: "Hecho",
            customMessage: next
              ? "Edición activada (visible en listados públicos si está publicada)."
              : "Edición desactivada.",
          });
          setRefresh(true);
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se pudo cambiar el estado activo de la edición.",
        });
      });
  }, []);

  const edicionesOrdenadas = useMemo(
    () => [...ediciones].sort((a, b) => Number(b.year) - Number(a.year)),
    [ediciones]
  );

  const abrirDialogoNuevaEdicion = () => {
    setNuevaEdicionModo("cero");
    setPlantillaId("");
    setOpenNuevaEdicionDialog(true);
  };

  const confirmarNuevaEdicion = () => {
    if (nuevaEdicionModo === "plantilla") {
      if (!plantillaId) {
        alertError({
          customMessage: "Elige una edición para copiar como base.",
        });
        return;
      }
      setOpenNuevaEdicionDialog(false);
      history.push(`/edit-congreso-edicion?plantilla=${plantillaId}`);
      return;
    }
    setOpenNuevaEdicionDialog(false);
    history.push("/edit-congreso-edicion");
  };

  const buttonFormatter = useCallback(
    (cell) => {
      const row = edicionesOrdenadas.find((e) => String(e._id) === String(cell));
      const showDeleteBtn = row && canDeleteRow(row);
      return (
        <div className="congreso-ediciones-actions">
          <Button
            variant="outlined"
            color="default"
            size="small"
            style={{ ...actionTextBtnStyle, marginLeft: 0 }}
            onClick={() => {
              history.push("/edit-congreso-edicion/" + cell);
            }}
          >
            <EditIcon fontSize="small" style={{ marginRight: 4, fontSize: 17 }} />
            Editar
          </Button>
          <Button
            variant="outlined"
            size="small"
            style={{
              ...actionTextBtnStyle,
              color: "#f3691a",
              borderColor: "#f3691a",
            }}
            onClick={() => history.push("/ver-congreso-edicion/" + cell)}
            aria-label="Gestionar edición"
          >
            <VisibilityIcon fontSize="small" style={{ marginRight: 4, fontSize: 17 }} />
            Gestionar
          </Button>
          <Tooltip title="Abrir la web de congresos como publicada (enlace temporal, ~30 min; incluye borradores)">
            <Button
              style={buttonsStyle}
              size="small"
              onClick={() => abrirPreviewWeb(cell)}
              aria-label="Ver preview web del congreso"
            >
              <OpenInNewIcon />
            </Button>
          </Tooltip>
          {showDeleteBtn ? (
            <Tooltip title="Eliminar">
              <Button
                style={buttonsStyle}
                size="small"
                onClick={() => {
                  setEdicionTarget({ _id: cell });
                  setOpenConfirmDialog(2);
                }}
              >
                <DeleteIcon />
              </Button>
            </Tooltip>
          ) : null}
        </div>
      );
    },
    [edicionesOrdenadas, history, canDeleteRow, abrirPreviewWeb]
  );

  const columns = useMemo(
    () => [
      { dataField: "year", text: "Año", sort: true },
      { dataField: "titulo", text: "Título", sort: true },
      {
        dataField: "isDefault",
        text: "Por defecto",
        sort: true,
        formatter: (cell, row) => (
          <Tooltip
            title={
              cell
                ? "Esta es la edición predeterminada"
                : "Marcar como predeterminada (webcongresos)"
            }
          >
            <span>
              <Switch
                color="primary"
                checked={!!cell}
                disabled={!!cell || !canDefaultRow(row)}
                onChange={(e) => {
                  if (e.target.checked && canDefaultRow(row)) {
                    setEdicionTarget(row);
                    setOpenConfirmDialog(3);
                  }
                }}
              />
            </span>
          </Tooltip>
        ),
      },
      {
        dataField: "published",
        text: "Publicada",
        sort: true,
        formatter: (cell, row) => (
          <Tooltip title={cell ? "Publicada" : "No publicada"}>
            <span>
              <Switch
                color="primary"
                checked={!!cell}
                disabled={!canPublishRow(row)}
                onChange={() => {
                  if (!canPublishRow(row)) return;
                  togglePublished(row);
                }}
              />
            </span>
          </Tooltip>
        ),
      },
      {
        dataField: "active",
        text: "Activa",
        sort: true,
        formatter: (cell, row) => (
          <Tooltip
            title={
              cell
                ? "Activa (puede mostrarse en la web y habilitar inscripción en webcongresos)"
                : "Inactiva"
            }
          >
            <span>
              <Switch
                color="primary"
                checked={!!cell}
                disabled={!canToggleActiveRow(row)}
                onChange={() => {
                  if (!canToggleActiveRow(row)) return;
                  toggleActive(row);
                }}
              />
            </span>
          </Tooltip>
        ),
      },
      {
        dataField: "updatedAt",
        text: "Última modificación",
        sort: true,
        formatter: dateFormatter,
      },
      {
        dataField: "id",
        text: "Acciones",
        formatter: buttonFormatter,
        headerStyle: { minWidth: 320, width: "22%" },
        style: { minWidth: 320, whiteSpace: "nowrap", verticalAlign: "middle" },
      },
    ],
    [
      buttonFormatter,
      canDefaultRow,
      canPublishRow,
      canToggleActiveRow,
      togglePublished,
      toggleActive,
    ]
  );

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getCongresoEdiciones();
        if (res.status === 200) {
          setEdiciones(res.data);
          setRefresh(false);
        }
      } catch (error) {
        alertError({
          error: error,
          customMessage: "No se pudieron obtener las ediciones de congreso.",
        });
        history.push("/dashboard");
      }
    };
    fetch();
  }, [refresh, history]);

  return (
    <>
      <Card>
        <CardHeader title="Lista de ediciones de congreso">
          <CardHeaderToolbar>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: "0.5rem" }}>
              <Button
                variant="outlined"
                component="a"
                href={toAbsoluteUrl("/manual_congresos.pdf")}
                download="manual_congresos.pdf"
                startIcon={<GetAppIcon />}
                style={{
                  borderColor: "#f3691a",
                  color: "#f3691a",
                }}
              >
                Descargar manual
              </Button>
              {canShowCreate && (
                <Button
                  variant="contained"
                  onClick={abrirDialogoNuevaEdicion}
                  startIcon={<AddIcon />}
                  style={{
                    backgroundColor: "#f3691a",
                    color: "#fff",
                  }}
                >
                  Añadir nuevo
                </Button>
              )}
            </div>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody style={{ paddingTop: "15px" }}>
          <Dialog
            open={openNuevaEdicionDialog}
            onClose={() => setOpenNuevaEdicionDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Nueva edición de congreso</DialogTitle>
            <DialogContent>
              <RadioGroup
                value={nuevaEdicionModo}
                onChange={(e) => {
                  setNuevaEdicionModo(e.target.value);
                  if (e.target.value === "cero") setPlantillaId("");
                }}
              >
                <FormControlLabel
                  value="cero"
                  control={<Radio color="primary" />}
                  label="Desde cero (formulario vacío)"
                />
                <FormControlLabel
                  value="plantilla"
                  control={<Radio color="primary" />}
                  label="A partir de otra edición (copiar contenido y ajustar año)"
                  disabled={!edicionesOrdenadas.length}
                />
              </RadioGroup>
              {nuevaEdicionModo === "plantilla" ? (
                <FormControl
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  disabled={!edicionesOrdenadas.length}
                >
                  <InputLabel id="plantilla-congreso-label">Edición base</InputLabel>
                  <Select
                    labelId="plantilla-congreso-label"
                    label="Edición base"
                    value={plantillaId}
                    onChange={(e) => setPlantillaId(e.target.value)}
                    MenuProps={{
                      disablePortal: true,
                      getContentAnchorEl: null,
                      PaperProps: { style: { maxHeight: 320 } },
                    }}
                  >
                    {edicionesOrdenadas.map((ed) => {
                      const id = ed._id != null ? String(ed._id) : "";
                      if (!id) return null;
                      return (
                        <MenuItem key={id} value={id}>
                          {ed.year} — {getNonEmpty(ed.titulo) || "Sin título"}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              ) : null}
              {!edicionesOrdenadas.length ? (
                <p className="text-muted small mt-2 mb-0">
                  No hay ediciones existentes; solo puedes crear desde cero.
                </p>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenNuevaEdicionDialog(false)}>
                Cancelar
              </Button>
              <Button
                onClick={confirmarNuevaEdicion}
                color="primary"
                variant="contained"
                style={{ backgroundColor: "#f3691a", color: "#fff" }}
              >
                Continuar
              </Button>
            </DialogActions>
          </Dialog>

          {!refresh && (
            <Table
              data={getData(ediciones)}
              columns={columns}
              pageKey="congresosPage"
              searchText=""
              onSearchChange={() => {}}
              defaultSorted={[{ dataField: "year", order: "desc" }]}
            />
          )}

          <ConfirmDialog
            title="¿Eliminar esta edición de congreso?"
            open={openConfirmDialog === 2}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              deleteCongresoEdicion(edicionTarget._id)
                .then((res) => {
                  if (res.status === 204 || res.status === 200) {
                    alertSuccess({
                      title: "Eliminado",
                      customMessage: "Edición eliminada correctamente.",
                    });
                    setRefresh(true);
                  }
                })
                .catch((error) => {
                  alertError({
                    error: error,
                    customMessage: "No se pudo eliminar la edición.",
                  });
                });
            }}
          />

          <ConfirmDialog
            title="¿Marcar esta edición como la predeterminada en webcongresos?"
            open={openConfirmDialog === 3}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              setDefaultCongresoEdicion(edicionTarget._id)
                .then((res) => {
                  if (res.status === 200) {
                    alertSuccess({
                      title: "Hecho",
                      customMessage: "Edición marcada como predeterminada.",
                    });
                    setRefresh(true);
                  }
                })
                .catch((error) => {
                  alertError({
                    error: error,
                    customMessage: "No se pudo marcar como predeterminada.",
                  });
                });
            }}
          />
        </CardBody>
      </Card>
    </>
  );
}
